import { Uhrwechsel } from './hooks/useUhrwechsel'
import { Suspense, lazy, useEffect } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { ApiError, ohneNetz } from './api/client'
import { useLogout, useMe } from './hooks/queries'
import { useOutboxVersand } from './hooks/useOutbox'
import { vorratLeeren } from './lib/vorrat'
import { Shell } from './features/shell/Shell'
import { BuchPage } from './features/export/BuchPage'
import { PrivatbuchPage } from './features/export/PrivatbuchPage'
import { LoginPage } from './features/auth/LoginPage'
import { RegisterPage } from './features/auth/RegisterPage'
import { JoinPage } from './features/auth/JoinPage'
import { ForgotPage } from './features/auth/ForgotPage'
import { ResetPage } from './features/auth/ResetPage'
import { ThreadPage } from './features/thread/ThreadPage'
import { NewTripPage } from './features/trips/NewTripPage'
import { TripInfoPage } from './features/trips/TripInfoPage'
import { SuchePage } from './features/thread/SuchePage'
import { FotosPage } from './features/trips/FotosPage'
import { AbstimmungenPage } from './features/trips/AbstimmungenPage'
/* Die Playlist wiegt nichts Eigenes -- sie zeichnet die Musikkarte, die
   der Chat ohnehin mitbringt. Ein eigenes Stück nachzuladen kostete eine
   Anfrage und spart nichts; die Karte lädt Leaflet, deshalb ist sie lazy. */
import { PlaylistPage } from './features/playlist/PlaylistPage'
import { SettingsPage } from './features/settings/SettingsPage'
import { PeoplePage } from './features/settings/PeoplePage'
import { AdministrationPage } from './features/settings/AdministrationPage'
import { BetriebPage } from './features/settings/BetriebPage'
import { StartPane } from './features/shell/StartPane'
import { StandWache } from './features/shell/StandWache'
import { Startanzeige } from './components/Startanzeige'
import { Appknopf } from './components/Appknopf'
import { useTexte } from './texte'
import { useMaintenance } from './lib/maintenance'
import { MaintenancePage } from './features/shell/MaintenancePage'

/**
 * Die Karte kommt erst, wenn jemand sie öffnet.
 *
 * Leaflet wiegt rund 150 kB — mehr als ein Drittel der bisherigen Anwendung.
 * Wer nur liest und antwortet, soll das nicht über Mobilfunk mitladen. Der
 * Chat ist der Alltag, die Karte ist der Besuch.
 */
const KartePage = lazy(() =>
  import('./features/map/KartePage').then((m) => ({ default: m.KartePage })),
)
const KarteEditPage = lazy(() =>
  import('./features/map/KartePage').then((m) => ({ default: m.KarteEditPage })),
)

export function App() {
  const maintenance = useMaintenance()
  const location = useLocation()

  /*
   * **The maintenance page instead of everything** (#131) -- except the
   * login form, the way in for whoever passes. The server said 503 with
   * `maintenance`; nothing behind it would get another answer, and the
   * outbox's sender (in `RequireAuth`) is unmounted with the rest.
   */
  if (maintenance !== null && location.pathname !== '/login') {
    return <MaintenancePage maintenance={maintenance} />
  }

  return (
    /*
     * **Die Uhr des Geräts liegt über allem** (#73).
     *
     * Ein Context und kein Haken in `Shell`: react-router gibt dem `<Outlet/>`
     * dasselbe Element-Objekt zurück, und React überspringt einen Teilbaum,
     * dessen Element sich nicht geändert hat. Ein Zustandswechsel in `Shell`
     * blieb damit an der Route hängen — gemessen nebeneinander im selben
     * Fenster: die Chatliste sprang von 19:26 auf 22:56, die Sprechblase
     * dahinter blieb auf 19:26. Ein Context kennt diese Sperre nicht.
     */
    <Uhrwechsel>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot" element={<ForgotPage />} />
      {/* Der Link aus der E-Mail. Er trägt das Token im Pfad und funktioniert
          ohne Anmeldung -- wer sich anmelden könnte, bräuchte ihn nicht. */}
      <Route path="/reset/:token" element={<ResetPage />} />
      {/* Der Einladungslink funktioniert auch ohne Konto -- wer ihn öffnet,
          wird zuerst durch Registrierung oder Anmeldung geführt. */}
      <Route path="/join/:code" element={<JoinPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<Shell />}>
          <Route index element={<StartPane />} />
          <Route path="t/:tripId" element={<ThreadPage />} />
          <Route path="t/:tripId/with/:userId" element={<ThreadPage />} />
          {/* The draft of the report (#85): the chat page with the draft as its list. */}
          <Route path="t/:tripId/entwurf" element={<ThreadPage entwurf />} />
          <Route
            path="t/:tripId/karte"
            element={
              <Suspense fallback={<KarteLaedt />}>
                <KartePage />
              </Suspense>
            }
          />
          {/* Die Werkbank des Autors: derselbe Bau, anderes Werkzeug. Wer
              sie ohne Recht aufruft, sieht die Karte ohne Knöpfe -- der Server
              entscheidet, nicht der Weg. */}
          <Route
            path="t/:tripId/karte/bearbeiten"
            element={
              <Suspense fallback={<KarteLaedt />}>
                <KarteEditPage />
              </Suspense>
            }
          />
          <Route path="t/:tripId/info" element={<TripInfoPage />} />
          <Route path="t/:tripId/suche" element={<SuchePage />} />
          <Route path="t/:tripId/fotos" element={<FotosPage />} />
          <Route path="t/:tripId/abstimmungen" element={<AbstimmungenPage />} />
          {/* Die Playlist der Reise (#102): der Reiter steht nur, wenn die
              Reise sie hat -- die Adresse führt trotzdem hin, und was
              darauf zu sehen ist, entscheidet der Server. */}
          <Route path="t/:tripId/playlist" element={<PlaylistPage />} />
          <Route path="new" element={<NewTripPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/administration" element={<AdministrationPage />} />
          <Route path="settings/betrieb" element={<BetriebPage />} />
          {/* Die Adresse hieß einen Tag lang so und ist im Betrieb; eine, die
              einmal galt, führt nicht ins Leere. Dieselbe Begründung trägt
              schon `settings/people` weiter unten. */}
          <Route
            path="settings/verwaltung"
            element={<Navigate to="/settings/administration" replace />}
          />
          {/* Der alte Weg bleibt und leitet weiter -- er steht in Lesezeichen
              und in einem Browserlauf (#39). */}
          <Route path="settings/people" element={<PeoplePage />} />
        </Route>
      </Route>

      {/* **Das Buch steht außerhalb der `Shell`** (#14): es ist die ganze
          Seite und braucht keine Chatliste daneben -- gedruckt würde sie
          mitkommen. Wer es sehen darf, entscheidet der Server: `?with=` lässt
          `partner()` nur beim Absender zu, und ohne `with` liefert
          `Visibility::scope()` ihm ausschließlich Beiträge an alle. */}
      <Route element={<RequireAuth />}>
        <Route path="t/:tripId/buch" element={<BuchPage />} />
        <Route path="t/:tripId/buch/privat/:userId" element={<PrivatbuchPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Uhrwechsel>
  )
}

/**
 * Schleuse vor allem, was eine Anmeldung braucht.
 *
 * Der Weg, den jemand aufgerufen hat, wird an die Anmeldeseite weitergereicht,
 * damit ein geteilter Link nach dem Anmelden nicht auf der Startseite endet.
 *
 * # „Abgemeldet" und „kein Netz" sind zweierlei (#13)
 *
 * Hier stand einmal `if (me.isError || !me.data)`, und damit war beides
 * dasselbe: ohne Netz scheitert `useMe()` sofort, und der Kaltstart im
 * Funkloch landete auf einem Anmeldeformular, das ohne Netz nichts ausrichten
 * kann. Genau die Sackgasse, um die es in #13 geht -- eine zwischengespeicherte
 * Hülle allein hätte sie nur schöner gemacht.
 *
 * Die Sitzung liegt beim Server (`server/src/Auth.php`). **Ein 401 ist deshalb
 * der einzige verlässliche Beweis dafür, dass dieses Gerät nicht mehr darf** --
 * und dann muss auch der Vorrat weg, sonst zeigte das Gerät nach einem Widerruf
 * weiter, was es zuletzt gesehen hat. Ein Fehlschlag ohne Antwort beweist
 * dagegen gar nichts.
 */
function RequireAuth() {
  const location = useLocation()
  const me = useMe()
  const t = useTexte().rahmen
  // Der Postbote sitzt hier und nicht im Chat: was ohne Netz geschrieben
  // wurde, soll auch dann rausgehen, wenn die App auf der Liste steht.
  useOutboxVersand()

  const client = useQueryClient()
  const abgemeldet = me.error instanceof ApiError && me.error.unauthorized

  // Der Widerruf wirkt sofort, nicht erst beim nächsten Start. Im Effekt und
  // nicht im Rumpf: ein Seiteneffekt beim Zeichnen liefe unter React zweimal.
  //
  // **Und der Zwischenspeicher muss mit**, nicht nur die Platte. Der Prüflauf
  // hat gezeigt, warum: `LoginPage` schickt sofort zurück, solange `me.data`
  // dasteht -- und nach dem Einlesen des Vorrats stand es da, obwohl der
  // Server gerade widersprochen hatte. Die Anmeldeseite warf einen also
  // zurück auf die Startseite, die zur Anmeldeseite schickte. Vorher konnte
  // das nicht passieren: ein Neuladen leerte den Zwischenspeicher ohnehin.
  //
  // Dass der Effekt sich damit nicht selbst nachtriggert, hängt an seiner
  // Abhängigkeit: `abgemeldet` bleibt wahr, und ein unveränderter Wert löst
  // keinen zweiten Lauf aus.
  useEffect(() => {
    if (!abgemeldet) return
    client.clear()
    void vorratLeeren()
  }, [abgemeldet, client])

  if (me.isPending) {
    return <Startanzeige />
  }

  // Der Server hat widersprochen -- das gilt, auch wenn aus dem Vorrat noch
  // Daten dastehen. Nach einem Fehlschlag behält react-query die alten.
  if (abgemeldet) {
    /*
     * **Mit Suche und nicht nur mit dem Pfad.** Hier stand `location.pathname`,
     * und damit verlor der Umweg über die Anmeldung genau das, was das Ziel
     * ausmacht: `?anfragen=1` aus einer Benachrichtigung, `?bereich=…` aus
     * einem weitergegebenen Verweis. Man landete auf der richtigen Seite und
     * an der falschen Stelle -- dieselbe Art Verlust wie beim Hash, den
     * `alsPfad()` wegwirft.
     */
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  if (!me.data) {
    // **Kein Formular, wo keins helfen kann.** Ohne Netz und ohne gemerkten
    // Stand weiß dieses Gerät nicht, wer hier ist -- die Anmeldeseite wüsste es
    // aber auch nicht und käme keinen Schritt weiter.
    if (ohneNetz(me.error)) {
      return (
        <div className="empty">
          <p className="empty__title">{t.keinNetz}</p>
          <p>{t.ohneVorrat}</p>
        </div>
      )
    }

    /*
     * **Only a 401 sends to the form** (1.78.0), as the head of this function
     * says. A 5xx here is the host that stumbles: after a new release the
     * store of the last state is empty (`vorrat.ts` throws away a store of
     * another build), and a 503 at that first start put a signed-in person in
     * front of the login form -- where signing in failed the same way.
     */
    if (me.error instanceof ApiError && me.error.status >= 500) {
      return (
        <div className="empty" data-schluessel="serverstockt">
          <p className="empty__title">{t.serverStockt}</p>
          <p>{t.serverStocktText}</p>
          <button className="button button--outline" type="button" onClick={() => void me.refetch()}>
            {t.nochmal}
          </button>
        </div>
      )
    }

    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  // Wer angemeldet ist, aber in keiner Reise steht, kommt nicht in die
  // Anwendung -- auch nicht in eine leere. Das ist der Fall des Fremden vom
  // weitergereichten Link: sein Konto gibt es, hereingelassen hat ihn niemand.
  if (!me.data.may_use_app) {
    return (
      <>
        <StandWache />
        <Wartetafel />
      </>
    )
  }

  return (
    <>
      <StandWache />
      <Outlet />
    </>
  )
}

/**
 * Was jemand sieht, der angemeldet ist und auf eine Bestätigung wartet.
 *
 * Nur diese eine Karte, nicht die Chatliste dahinter.
 *
 * **Sie sagt, was jetzt gilt** (1.91.0). Aus dem Betrieb: man stand hier und
 * wusste nicht, was zu tun ist, und der einzige Knopf war „Abmelden“. Eine
 * Bestätigung kann Stunden dauern; ein Nachfragen im Takt hilft da nicht,
 * eine Mail schon (`AdmissionMail`). Die Tafel nennt sie, wenn der Server
 * sie verschicken kann (`mail_on_admission`), und sagt, dass man die App
 * schließen darf -- schließen kann sie sich nicht selbst, iOS ignoriert
 * `window.close()`. Wer sie danach öffnet, wird neu gefragt
 * (`refetchOnMount`) und ist drin.
 *
 * **Ein Weg hinaus bleibt**, leise: für ein geteiltes Gerät oder eine
 * vertippte Adresse. Jemanden anzumelden und ihm dann keinen Ausgang zu
 * lassen, wäre eine Falle. Wohin es danach geht, sagt `useLogout()`.
 *
 * Wer abgelehnt wurde, sieht dieselbe Tafel. Das ist keine Nachlässigkeit,
 * sondern dieselbe Entscheidung wie überall: eine mitgeteilte Ablehnung ist
 * eine Aufforderung nachzufragen.
 */
function Wartetafel() {
  const texte = useTexte()
  const t = texte.rahmen.warten
  const me = useMe()
  const logout = useLogout()
  const wartet = me.data?.waiting_for ?? null

  /*
   * **One child for `.centered`, and the three parts inside it** (1.92.0).
   * `.centered` is a flex row built for one card; in 1.91.0 the card, the app
   * card and the link stood side by side and ran off both edges -- from use,
   * with a screenshot from Safari on the iPhone. The bench did not see it:
   * headless Chromium offers no installation, so the app card was not there.
   */
  return (
    <div className="centered">
      <div className="wartetafel" data-wartetafel-rahmen="">
      <div className="card" data-wartetafel={wartet ? 'anfrage' : 'nichts'}>
        <h1>{wartet?.trip_title ?? t.nichts}</h1>
        {wartet ? (
          <>
            <p className="card__lead">{t.angefragt(wartet.owner_name)}</p>
            <p className="card__lead" data-schluessel={me.data?.mail_on_admission ? 'warten-mail' : 'warten-ohne-mail'}>
              {me.data?.mail_on_admission ? t.mailKommt(me.data.email) : t.ohneMail}
            </p>
          </>
        ) : (
          <p className="card__lead">{t.eingeladenWerden}</p>
        )}
        <p className="leise">
          {t.angemeldetAls(me.data?.display_name ?? '', me.data?.email ?? '')}
        </p>
      </div>
      <Appknopf />
      <button
        className="button button--quiet button--klein button--mittig"
        type="button"
        data-schluessel="warten-anderes-konto"
        disabled={logout.isPending}
        onClick={() => logout.mutate()}
      >
        {t.anderesKonto}
      </button>
      </div>
    </div>
  )
}

/**
 * Was steht, während die Kartenseite nachgeladen wird.
 *
 * **Als eigenes Bauteil und nicht als Ausdruck im `fallback`**: der Wortlaut
 * kommt aus `useTexte()`, und ein Hook lässt sich in ein `<div>` mitten in
 * einem Ausdruck nicht setzen. Nebenbei steht der Satz damit einmal statt
 * zweimal — die Karte hängt an zwei Routen.
 */
function KarteLaedt() {
  return <div className="empty">{useTexte().rahmen.karteLaedt}</div>
}
